import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  GraduationCap, BookOpen, Users, FileText, CheckCircle2, XCircle,
  Loader2, TrendingUp, Sparkles, Wallet, Activity, Mail, Phone, Building2, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = () => axios.create({
  baseURL: API,
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const Card = ({ title, value, icon: Icon, accent = 'navy', sub, testid }) => {
  const accents = {
    navy: 'from-[#1A2A4F] to-[#2C4078] text-white',
    amber: 'from-[#F5A623] to-[#E08F0C] text-white',
    emerald: 'from-emerald-500 to-emerald-600 text-white',
    rose: 'from-rose-500 to-rose-600 text-white',
    white: 'bg-white text-[#1A2A4F] border border-gray-100',
  };
  const isGradient = accent !== 'white';
  return (
    <div
      className={`rounded-2xl p-5 ${isGradient ? `bg-gradient-to-br ${accents[accent]}` : accents.white} shadow-sm`}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`text-xs font-bold uppercase tracking-wider ${isGradient ? 'opacity-80' : 'text-gray-500'}`}>
          {title}
        </div>
        {Icon && <Icon className={`w-5 h-5 ${isGradient ? 'opacity-80' : 'text-[#F5A623]'}`} />}
      </div>
      <div className="text-3xl font-bold tabular-nums" data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
      {sub && (
        <div className={`text-xs mt-1.5 ${isGradient ? 'opacity-75' : 'text-gray-500'}`}>{sub}</div>
      )}
    </div>
  );
};

const StatusPill = ({ status, count }) => {
  const pretty = (s) => (s || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg text-sm border border-gray-100">
      <span className="text-gray-700">{pretty(status)}</span>
      <span className="font-bold tabular-nums text-[#1A2A4F]">{count}</span>
    </div>
  );
};

const AgentDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api().get('/agent-portal/me/dashboard');
        if (alive) setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load dashboard');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="agent-dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
      </div>
    );
  }
  if (!data) {
    return <div className="p-10 text-center text-gray-500">Could not load your dashboard.</div>;
  }

  const { profile, common, analytics } = data;
  const fmt = (n) => (n ?? 0).toLocaleString();

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto" data-testid="agent-dashboard">
      {/* Header / Profile strip */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A2A4F]">Welcome back, {profile.name || 'Agent'}</h1>
          <p className="text-sm text-gray-500 mt-1">Here&apos;s a quick snapshot of your performance and what&apos;s happening across the network.</p>
        </div>
        <button
          onClick={() => navigate('/agent/applications?new=1')}
          className="inline-flex items-center gap-1.5 bg-[#F5A623] hover:bg-[#E08F0C] text-white text-sm font-bold px-4 py-2.5 rounded-md shadow-sm"
          data-testid="dashboard-new-app-btn"
        >
          <span className="text-lg leading-none">+</span> New Application
        </button>
      </div>

      {/* Profile card */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6" data-testid="agent-profile-card">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1A2A4F] to-[#F5A623] text-white flex items-center justify-center font-bold text-xl">
              {(profile.name || profile.email || 'A').trim().slice(0, 1).toUpperCase()}
            </div>
            <div>
              <div className="text-lg font-bold text-[#1A2A4F]" data-testid="agent-profile-name">{profile.name}</div>
              <div className="text-xs text-gray-500 flex items-center gap-1.5"><Mail className="w-3 h-3" />{profile.email}</div>
            </div>
          </div>
          <div className="hidden md:block h-12 w-px bg-gray-100" />
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
            {profile.agency_name && (
              <div className="flex items-center gap-1.5 text-gray-600"><Building2 className="w-3.5 h-3.5 text-[#F5A623]" /><span className="font-semibold text-[#1A2A4F]">{profile.agency_name}</span></div>
            )}
            {profile.phone && (
              <div className="flex items-center gap-1.5 text-gray-600"><Phone className="w-3.5 h-3.5 text-[#F5A623]" />{profile.phone}</div>
            )}
            <div className="flex items-center gap-1.5 text-gray-600">
              <ShieldCheck className={`w-3.5 h-3.5 ${profile.is_active ? 'text-emerald-500' : 'text-rose-500'}`} />
              <span className="font-semibold">{profile.is_active ? 'Active' : 'Inactive'}</span>
              <span>·</span>
              <span>{profile.role}</span>
            </div>
            <div className="text-gray-500">
              Universities allocated: <span className="font-bold text-[#1A2A4F]">{profile.allocated_universities?.length || 0}</span> · Countries: <span className="font-bold text-[#1A2A4F]">{profile.allocated_countries?.length || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Common cards (network-wide reference) */}
      <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Network</h2>
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4 mb-6">
        <Card title="Total Universities" value={fmt(common.universities_total)} icon={GraduationCap} accent="navy" sub="Across the EduBridge network" testid="card-total-universities" />
        <Card title="Total Courses" value={fmt(common.courses_total)} icon={BookOpen} accent="amber" sub="Live programs available to refer" testid="card-total-courses" />
      </div>

      {/* Personal analytics */}
      <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">My Performance</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card title="My Students" value={fmt(analytics.my_students_total)} icon={Users} accent="white" sub={`+${fmt(analytics.my_students_this_month)} this month`} testid="card-my-students" />
        <Card title="My Applications" value={fmt(analytics.my_apps_total)} icon={FileText} accent="white" sub={`+${fmt(analytics.my_apps_this_month)} this month`} testid="card-my-applications" />
        <Card title="Approved" value={fmt(analytics.approved_count)} icon={CheckCircle2} accent="emerald" sub="Offers / accepted / enrolled" testid="card-approved" />
        <Card title="Rejected" value={fmt(analytics.rejected_count)} icon={XCircle} accent="rose" sub="Refused / declined" testid="card-rejected" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card
          title="Conversion Rate"
          value={`${analytics.conversion_rate}%`}
          icon={TrendingUp}
          accent="white"
          sub={`${fmt(analytics.approved_count)} of ${fmt(analytics.my_apps_total)} approved`}
          testid="card-conversion-rate"
        />
        <Card
          title="Commission Earned"
          value={`$${fmt(analytics.commission_total)}`}
          icon={Wallet}
          accent="white"
          sub="Lifetime, across all approved apps"
          testid="card-commission"
        />
        <Card
          title="AI Credits"
          value={fmt(analytics.ai_credits_balance)}
          icon={Sparkles}
          accent="white"
          sub="Available for AI assistance"
          testid="card-ai-credits"
        />
      </div>

      {/* Status breakdown + Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="status-breakdown">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-[#F5A623]" />
            <h3 className="text-sm font-bold text-[#1A2A4F]">By Status</h3>
          </div>
          {Object.keys(analytics.my_apps_by_status || {}).length === 0 ? (
            <p className="text-sm text-gray-400 italic">No applications yet — refer your first student to see this breakdown.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(analytics.my_apps_by_status).map(([s, c]) => (
                <StatusPill key={s} status={s} count={c} />
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="recent-activity">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#F5A623]" />
              <h3 className="text-sm font-bold text-[#1A2A4F]">Recent activity</h3>
            </div>
            <Link to="/agent/applications" className="text-xs font-semibold text-[#F5A623] hover:underline" data-testid="view-all-applications-link">View all</Link>
          </div>
          {(analytics.recent_activity || []).length === 0 ? (
            <p className="text-sm text-gray-400 italic">No recent activity.</p>
          ) : (
            <ul className="space-y-2">
              {analytics.recent_activity.map((a) => {
                const name = a.student_name || [a.first_name, a.last_name].filter(Boolean).join(' ') || 'Student';
                return (
                  <li key={a.id} className="flex items-start justify-between gap-3 text-sm py-2 border-b border-gray-50 last:border-0" data-testid={`recent-${a.id}`}>
                    <div className="min-w-0">
                      <div className="font-semibold text-[#1A2A4F] truncate">{name}</div>
                      <div className="text-xs text-gray-500 truncate">{a.program_name || '—'} · {a.university_name || '—'}</div>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-gray-100 text-gray-700 px-2 py-1 rounded">{(a.status || 'pending').replace(/_/g, ' ')}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentDashboardPage;
